/**
 * Educator Glass Light Theme
 *
 * Light theme on a flat warm-grey canvas with solid white cards.
 */

import { createGlassTheme } from './create-glass-theme';

export const educatorGlassLight = createGlassTheme({
  meta: {
    id: 'educator-glass-light',
    name: 'Warm Stone',
    description: 'Light theme with a warm grey canvas and white cards',
    category: 'educator',
    mode: 'light',
    previewColors: ['#edecea', '#ffffff', '#f7f7f5', '#c5c5c3'],
  },

  background: {
    base: '#edecea',
    gradient: {
      type: 'linear',
      stops: [
        { color: '#edecea', position: 0 },
        { color: '#edecea', position: 100 },
      ],
    },
    blur: '20px',
    backdropFilter: 'blur(20px) saturate(180%)',
  },

  accent: {
    primary: '#4351a8',
    primaryHover: '#36428c',
    primaryGradient: 'linear-gradient(135deg, #4351a8 0%, #4351a8 100%)',
    primaryGradientHover: 'linear-gradient(135deg, #36428c 0%, #36428c 100%)',
    secondary: '#c4836a',
    secondaryHover: '#b57358',
    secondaryGradient: 'linear-gradient(135deg, #8b4a3a, #c4836a, #e8a882)',
    secondaryGradientHover: 'linear-gradient(135deg, #743d30, #b57358, #d49670)',
    success: '#059669',
    successStrong: '#065f46',
    warning: '#d97706',
    warningStrong: '#92400e',
    error: '#dc2626',
    info: '#0891b2',
  },

  colors: {
    text: {
      primary: '#23211e',
      secondary: '#636362',
      tertiary: '#6b6a66',
      muted: '#a3a29e',
      inverse: '#fafaf9',
    },
    shadowBase: 'rgba(35, 33, 30',
    shadowOpacity: [0.08, 0.1, 0.12, 0.15],
    accentShadow: '0 8px 16px rgba(67, 81, 168, 0.25)',
    borderBase: 'rgba(35, 33, 30',
    borderOpacity: [0.06, 0.1, 0.15],
    card: {
      background: '#ffffff',
      backgroundHover: '#f7f7f5',
      border: '#e8e8e6',
      shadow: 'none',
    },
    nav: {
      background: '#edecea',
      border: '#c5c5c3',
      shadow: 'none',
      activeBackground: '#ffffff',
      activeText: '#23211e',
    },
    modal: {
      background: '#ffffff',
      border: '#e8e8e6',
      shadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      backdropColor: 'rgba(0, 0, 0, 0.4)',
    },
    dropdown: {
      background: '#ffffff',
      backgroundHover: '#f7f7f5',
      border: '#e8e8e6',
      shadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
    },
  },

  coverFallback: {
    gradient: 'linear-gradient(135deg, #eceef8 0%, #e3e6f4 100%)',
    text: '#4351a8',
    texture: 'repeating-conic-gradient(#4351a8 0 25%, #c7cce9 0 50%) 0/2px 2px, repeating-conic-gradient(#4351a8 0 25%, #c7cce9 0 50%) 1px 1px/2px 2px',
    textureOpacity: 0.08,
  },

  pageBackgrounds: {
    'lesson-command-center': {
      gradient: {
        type: 'linear',
        angle: -45,
        stops: [
          { color: '#e0f4f8', position: 0 },
          { color: '#e8f0fa', position: 25 },
          { color: '#ede4f5', position: 50 },
          { color: '#f5e8f0', position: 75 },
          { color: '#f8f4f0', position: 100 },
        ],
        animated: true,
        animationDuration: '15s',
      },
      gradientOverlay: {
        type: 'linear',
        angle: -45,
        stops: [
          { color: 'rgba(56, 189, 248, 0.08)', position: 0 },
          { color: 'rgba(139, 92, 246, 0.06)', position: 50 },
          { color: 'rgba(236, 72, 153, 0.05)', position: 100 },
        ],
        animated: false,
      },
      orbs: [
        {
          size: '320px',
          gradient: 'linear-gradient(135deg, rgba(56, 189, 248, 0.5), rgba(99, 102, 241, 0.4))',
          position: { top: '15%', left: '10%' },
          blur: '70px',
          opacity: 0.45,
          animationName: 'floatOrb1',
          animationDuration: '18s',
        },
        {
          size: '280px',
          gradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.45), rgba(168, 85, 247, 0.35))',
          position: { top: '55%', right: '15%' },
          blur: '65px',
          opacity: 0.4,
          animationName: 'floatOrb2',
          animationDuration: '20s',
        },
        {
          size: '350px',
          gradient: 'linear-gradient(135deg, rgba(236, 72, 153, 0.35), rgba(244, 114, 182, 0.25))',
          position: { bottom: '10%', left: '20%' },
          blur: '75px',
          opacity: 0.38,
          animationName: 'floatOrb3',
          animationDuration: '22s',
        },
        {
          size: '200px',
          gradient: 'linear-gradient(135deg, rgba(34, 211, 238, 0.4), rgba(56, 189, 248, 0.3))',
          position: { top: '35%', right: '30%' },
          blur: '55px',
          opacity: 0.35,
          animationName: 'floatOrb4',
          animationDuration: '16s',
        },
      ],
    },
    vision: {
      gradient: {
        type: 'linear',
        angle: -45,
        stops: [
          { color: '#e0f4f8', position: 0 },
          { color: '#e8f0fa', position: 25 },
          { color: '#ede4f5', position: 50 },
          { color: '#f5e8f0', position: 75 },
          { color: '#f8f4f0', position: 100 },
        ],
        animated: true,
        animationDuration: '15s',
      },
      gradientOverlay: {
        type: 'linear',
        angle: -45,
        stops: [
          { color: 'rgba(56, 189, 248, 0.08)', position: 0 },
          { color: 'rgba(139, 92, 246, 0.06)', position: 50 },
          { color: 'rgba(236, 72, 153, 0.05)', position: 100 },
        ],
        animated: false,
      },
      orbs: [
        {
          size: '320px',
          gradient: 'linear-gradient(135deg, rgba(56, 189, 248, 0.5), rgba(99, 102, 241, 0.4))',
          position: { top: '15%', left: '10%' },
          blur: '70px',
          opacity: 0.45,
          animationName: 'floatOrb1',
          animationDuration: '18s',
        },
        {
          size: '280px',
          gradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.45), rgba(168, 85, 247, 0.35))',
          position: { top: '55%', right: '15%' },
          blur: '65px',
          opacity: 0.4,
          animationName: 'floatOrb2',
          animationDuration: '20s',
        },
        {
          size: '350px',
          gradient: 'linear-gradient(135deg, rgba(236, 72, 153, 0.35), rgba(244, 114, 182, 0.25))',
          position: { bottom: '10%', left: '20%' },
          blur: '75px',
          opacity: 0.38,
          animationName: 'floatOrb3',
          animationDuration: '22s',
        },
        {
          size: '200px',
          gradient: 'linear-gradient(135deg, rgba(34, 211, 238, 0.4), rgba(56, 189, 248, 0.3))',
          position: { top: '35%', right: '30%' },
          blur: '55px',
          opacity: 0.35,
          animationName: 'floatOrb4',
          animationDuration: '16s',
        },
      ],
    },
  },
});

export default educatorGlassLight;
